import { Injectable, inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { DomSanitizer } from '@angular/platform-browser';
import { LanguageService } from './language.service';
import { DialogModel } from '../../shared/components/dialog/dialog';
import { environment } from '../../../environments/environment';

@Injectable({
  providedIn: 'root',
})
export class DialogService {
  private readonly dialog = inject(MatDialog);
  private readonly languageService = inject(LanguageService);
  private readonly sanitizer = inject(DomSanitizer);

  private readonly supportEmail = environment.general.hvsSupportContactEmail;

  closeAll() {
    this.dialog.closeAll();
  }

  showCheckoutInProgressDialog(
    goToCheckout: () => void,
    logout: () => void,
  ): void {
    const checkoutInProgressDialogModel = new DialogModel(this.dialog);
    checkoutInProgressDialogModel.headline =
      this.languageService.translateOnRuntime(
        'portal.checkoutInProgressDialogModel.headline',
      );
    checkoutInProgressDialogModel.closable = false;
    checkoutInProgressDialogModel.description =
      this.languageService.translateOnRuntime(
        'portal.checkoutInProgressDialogModel.description',
      );
    checkoutInProgressDialogModel.linkDescription =
      this.languageService.translateOnRuntime(
        'portal.checkoutInProgressDialogModel.linkDescription',
      );
    checkoutInProgressDialogModel.linkHref = `mailto:${this.supportEmail}`;
    checkoutInProgressDialogModel.footerDescription =
      this.languageService.translateOnRuntime(
        'portal.checkoutInProgressDialogModel.footerDescription',
      );
    checkoutInProgressDialogModel.footerLinkDescription =
      this.languageService.translateOnRuntime(
        'portal.checkoutInProgressDialogModel.footerLinkDescription',
      );
    checkoutInProgressDialogModel.footerLinkAction = () => {
      logout();
    };
    checkoutInProgressDialogModel.additionalFooterDescription =
      this.languageService.translateOnRuntime(
        'portal.checkoutInProgressDialogModel.additionalFooterDescription',
      );
    checkoutInProgressDialogModel.additionalFooterLinkDescription =
      this.languageService.translateOnRuntime(
        'portal.checkoutInProgressDialogModel.additionalFooterLinkDescription',
      );
    checkoutInProgressDialogModel.additionalFooterLinkAction = () => {
      goToCheckout();
    };

    checkoutInProgressDialogModel.openDialog();
  }

  showCanceledPlanDialog(logout: () => void, reactivatePlan: () => void): void {
    const canceledPlanDialog = new DialogModel(this.dialog);
    canceledPlanDialog.headline = this.languageService.translateOnRuntime(
      'canceledPlan.headline',
    );
    canceledPlanDialog.closable = false;

    canceledPlanDialog.description = `<p>${this.languageService.translateOnRuntime('canceledPlan.description')}</p><br><p class="whitespace-pre-wrap">${this.languageService.translateOnRuntime('canceledPlan.connectingText')}</p>`;

    canceledPlanDialog.additionalFooterDescription =
      this.languageService.translateOnRuntime('canceledPlan.footerDescription');
    canceledPlanDialog.additionalFooterLinkDescription =
      this.languageService.translateOnRuntime(
        'canceledPlan.footerLinkDescription',
      );

    canceledPlanDialog.footerDescription =
      this.languageService.translateOnRuntime(
        'canceledPlan.additionalFooterDescription',
      );
    canceledPlanDialog.footerLinkDescription =
      this.languageService.translateOnRuntime(
        'canceledPlan.additionalFooterLinkDescription',
        { supportEmail: this.supportEmail },
      );

    canceledPlanDialog.additionalFooterLinkAction = () => {
      logout();
    };

    canceledPlanDialog.footerLinkAction = () => {
      window.location.href = `mailto:${this.supportEmail}`;
    };

    canceledPlanDialog.topButtonFooter = {
      text: this.languageService.translateOnRuntime(
        'canceledPlan.reactivatePlan',
      ),
      action: () => {
        reactivatePlan();
        if (canceledPlanDialog.topButtonFooter) {
          canceledPlanDialog.topButtonFooter.isLoading = true;
          canceledPlanDialog.topButtonFooter.disabled = true;
        }
      },
    };

    canceledPlanDialog.openDialog();
  }

  showPaymentExpiredDialog(logout: () => void): void {
    const paymentExpiredDialog = new DialogModel(this.dialog);
    paymentExpiredDialog.headline = this.languageService.translateOnRuntime(
      'paymentExpired.headline',
    );
    paymentExpiredDialog.closable = false;
    paymentExpiredDialog.description = this.languageService.translateOnRuntime(
      'paymentExpired.description',
      {
        supportEmail: this.supportEmail,
      },
    );
    paymentExpiredDialog.linkDescription =
      this.languageService.translateOnRuntime(
        'paymentExpired.linkDescription',
        {
          supportEmail: this.supportEmail,
        },
      );
    paymentExpiredDialog.footerDescription =
      this.languageService.translateOnRuntime(
        'paymentExpired.footerDescription',
      );
    paymentExpiredDialog.footerLinkDescription =
      this.languageService.translateOnRuntime(
        'paymentExpired.footerLinkDescription',
      );

    paymentExpiredDialog.footerLinkAction = () => {
      logout();
    };

    paymentExpiredDialog.openDialog();
  }

  showGracePeriodDialog(daysLeft: number): void {
    const gracePeriodDialog = new DialogModel(this.dialog);
    gracePeriodDialog.closable = false;
    gracePeriodDialog.headline = this.languageService.translateOnRuntime(
      'gracePeriod.headline',
    );
    gracePeriodDialog.description = this.languageService.translateOnRuntime(
      'gracePeriod.description',
      {
        daysLeft: daysLeft,
      },
    );
    gracePeriodDialog.linkDescription = this.languageService.translateOnRuntime(
      'gracePeriod.linkDescription',
      {
        supportEmail: this.supportEmail,
      },
    );
    gracePeriodDialog.confirmButtonText =
      this.languageService.translateOnRuntime('gracePeriod.continue');
    gracePeriodDialog.confirmButtonAction = () => {
      gracePeriodDialog.closeDialog();
    };

    gracePeriodDialog.openDialog();
  }

  showMaintenanceDialog(): void {
    const maintenanceDialog = new DialogModel(this.dialog);
    maintenanceDialog.closable = false;
    maintenanceDialog.headline = this.languageService.translateOnRuntime(
      'maintenance.headline',
    );
    maintenanceDialog.description = this.languageService.translateOnRuntime(
      'maintenance.description',
    );
    maintenanceDialog.footerDescription =
      this.languageService.translateOnRuntime('maintenance.supportContact');
    maintenanceDialog.footerLinkDescription = this.supportEmail;

    maintenanceDialog.openDialog();
  }

  showRegistrationMaintenanceDialog(): void {
    const maintenanceDialog = new DialogModel(this.dialog);
    maintenanceDialog.closable = false;
    maintenanceDialog.headline = this.languageService.translateOnRuntime(
      'maintenanceRegistration.headline',
    );
    maintenanceDialog.description = this.languageService.translateOnRuntime(
      'maintenanceRegistration.description',
    );
    maintenanceDialog.footerDescription =
      this.languageService.translateOnRuntime(
        'maintenanceRegistration.supportContact',
      );
    maintenanceDialog.footerLinkDescription = this.supportEmail;
    maintenanceDialog.footerDescriptionEnding =
      this.languageService.translateOnRuntime(
        'maintenanceRegistration.supportEnding',
      );

    maintenanceDialog.openDialog();
  }
}
